import { NetworkError } from '../../lib/errors.ts';
import { proxyFetch as defaultProxyFetch, type ProxyFetchOpts } from '../../lib/proxy.ts';
import { md5 } from '../_venera/convert.ts';
import { loadVeneraPlugin } from '../_venera/load.ts';
import { mapVeneraComicCard, mapVeneraPage, mapVeneraPageResult } from '../_venera/mapper.ts';
import type { VeneraComicSource } from '../_venera/types.ts';
import type {
  ComicCard,
  ComicDetails,
  ComicSource,
  ListOpts,
  Page,
  PageRef,
  PopularOpts,
  RecommendationOpts,
  SearchFiltersSchema,
  SearchQuery,
  SourceAuth,
  SourceCommentOpts,
  SourceCommentPage,
  SourceId,
} from '../types.ts';

export const JMCOMIC_SOURCE_ID = 'jmcomic' satisfies SourceId;

export const JM_API_DOMAINS = [
  'www.cdnhjk.net',
  'www.cdngwc.cc',
  'www.cdngwc.net',
  'www.cdngwc.club',
] as const;

const JM_ALBUM_HOSTS = ['18comic.vip', 'jmcomic.me'] as const;

const JM_MIRROR_TIMEOUT_MS = 1_500;
let preferredJmHost: string | undefined;

const JM_DEFAULTS = {
  refreshDomainsOnStart: false,
  dailyCheckInTask: false,
  apiDomain: '1',
  imageStream: '1',
  favoriteOrder: 'mr',
} as const;

const JM_SEARCH_FILTERS: SearchFiltersSchema = {
  fields: [
    {
      id: 'sort',
      type: 'select',
      label: 'Sort',
      defaultValue: 'mr',
      options: [
        { value: 'mr', label: 'Latest' },
        { value: 'mv', label: 'All-time rank' },
        { value: 'mv_m', label: 'Monthly rank' },
        { value: 'mv_w', label: 'Weekly rank' },
        { value: 'mv_t', label: 'Daily rank' },
        { value: 'mp', label: 'Most pages' },
        { value: 'tf', label: 'Most liked' },
      ],
    },
  ],
};

const JM_RANK_OPTIONS = [
  { value: 'mv', label: 'All time' },
  { value: 'mv_m', label: 'Monthly' },
  { value: 'mv_w', label: 'Weekly' },
] as const;

const JM_SORT_OPTIONS = [
  { value: 'mr', label: 'Latest' },
  { value: 'mv', label: 'All-time rank' },
  { value: 'mv_m', label: 'Monthly rank' },
  { value: 'mv_w', label: 'Weekly rank' },
  { value: 'mv_t', label: 'Daily rank' },
  { value: 'mp', label: 'Most pages' },
  { value: 'tf', label: 'Most liked' },
] as const;

const JM_CATEGORY_OPTIONS = [
  { value: '0', label: 'Latest A-manga', category: '最新A漫' },
  { value: 'doujin', label: 'Doujin', category: '同人' },
  { value: 'single', label: 'Single', category: '單本' },
  { value: 'short', label: 'Short', category: '短篇' },
  { value: 'another', label: 'Other', category: '其他類' },
  { value: 'hanman', label: 'Hanman', category: '韓漫' },
  { value: 'meiman', label: 'Western', category: '美漫' },
  { value: 'another_cosplay', label: 'Cosplay', category: 'Cosplay' },
  { value: '3D', label: '3D', category: '3D' },
  { value: '禁漫漢化組', label: 'JM translation', category: '禁漫漢化組' },
  { value: '無修正', label: 'Uncensored', category: '無修正' },
  { value: '全彩', label: 'Full color', category: '全彩' },
  { value: 'NTR', label: 'NTR', category: 'NTR' },
  { value: '教師', label: 'Teacher', category: '教師' },
  { value: '人妻', label: 'Married woman', category: '人妻' },
] as const;

const JM_WEEKLY_TYPE_OPTIONS = [
  { value: 'manga', label: 'Manga' },
  { value: 'hanman', label: 'Korean' },
  { value: 'another', label: 'Other' },
] as const;

const JM_RECOMMENDATION_OPTIONS = [
  { id: 'home', label: 'Home', default: true },
  {
    id: 'ranking',
    label: 'Ranking',
    controls: [
      {
        id: 'rank',
        type: 'chips',
        label: 'Rank',
        defaultValue: 'mv_w',
        options: [...JM_RANK_OPTIONS],
      },
    ],
  },
  {
    id: 'category',
    label: 'Category',
    controls: [
      {
        id: 'category',
        type: 'searchable-select',
        label: 'Category',
        defaultValue: '0',
        options: JM_CATEGORY_OPTIONS.map(({ value, label }) => ({ value, label })),
      },
      {
        id: 'sort',
        type: 'chips',
        label: 'Sort',
        defaultValue: 'mr',
        options: [...JM_SORT_OPTIONS],
      },
    ],
  },
  {
    id: 'weekly',
    label: 'Weekly picks',
    controls: [
      {
        id: 'type',
        type: 'chips',
        label: 'Type',
        defaultValue: 'manga',
        options: [...JM_WEEKLY_TYPE_OPTIONS],
      },
    ],
  },
] satisfies NonNullable<ComicSource['recommendationOptions']>;

const capabilities = {
  login: true,
  favorites: true,
  multiFavCategories: true,
  search: true,
  advancedSearch: true,
  recommendations: true,
  comments: true,
  loginNotes:
    'JMComic uses username/password login. Bunko sends credentials to JMComic through the proxy worker once, then stores only the returned plugin token state in encrypted source auth.',
} satisfies ComicSource['capabilities'];

export interface CreateJmcomicSourceOptions {
  jsSource: string;
  proxyFetch?: (url: string, opts?: ProxyFetchOpts) => Promise<Response>;
  saveAuth?: (sourceId: SourceId, auth: SourceAuth, opts?: { username?: string }) => Promise<void>;
}

export function createJmcomicSource(options: CreateJmcomicSourceOptions): ComicSource {
  let delegatePromise: Promise<VeneraComicSource> | undefined;
  const baseProxyFetch = options.proxyFetch ?? defaultProxyFetch;

  async function delegate(): Promise<VeneraComicSource> {
    delegatePromise ??= loadVeneraPlugin({
      sourceId: JMCOMIC_SOURCE_ID,
      jsSource: jmPluginBootstrap(options.jsSource),
      defaults: JM_DEFAULTS,
      settings: JM_DEFAULTS,
      capabilities,
      searchFilters: JM_SEARCH_FILTERS,
      proxyFetch: (url, opts) => jmProxyFetch(url, opts, baseProxyFetch),
      saveAuth: options.saveAuth,
    });
    return delegatePromise;
  }

  const source: ComicSource = {
    id: JMCOMIC_SOURCE_ID,
    name: '禁漫天堂',
    description: 'JMComic / 18comic via the pinned Venera plugin with browser-side row deshuffling.',
    capabilities,
    searchFilters: JM_SEARCH_FILTERS,
    recommendationOptions: JM_RECOMMENDATION_OPTIONS,

    async search(query: SearchQuery, auth?: SourceAuth): Promise<Page<ComicCard>> {
      if (isEmptyQuery(query)) {
        return source.getPopular({ page: query.page }, auth);
      }
      return (await delegate()).search(withDefaultSort(query), auth);
    },

    async getPopular(opts?: PopularOpts, auth?: SourceAuth): Promise<Page<ComicCard>> {
      const page = Math.max(1, opts?.page ?? 1);
      const result = await (await delegate()).testInternals.callPluginMethod('explore.0.load', [page], auth);
      return mapJmExploreResult(result, page);
    },

    async getRecommendations(opts?: RecommendationOpts, auth?: SourceAuth): Promise<Page<ComicCard>> {
      const loaded = await delegate();
      switch (opts?.optionId ?? 'home') {
        case 'ranking':
          return loadJmRankingRecommendations(loaded, opts, auth);
        case 'category':
          return loadJmCategoryRecommendations(loaded, opts, auth);
        case 'weekly':
          return loadJmWeeklyRecommendations(loaded, opts, auth);
        case 'home':
        default:
          return source.getPopular({ page: opts?.page, cursor: opts?.cursor }, auth);
      }
    },

    async getComic(id: string, auth?: SourceAuth): Promise<ComicDetails> {
      const normalizedId = normalizeJmId(id);
      const details = await (await delegate()).getComic(normalizedId, auth);
      return { ...details, sourceUrl: jmSourceUrl(normalizedId) };
    },

    async getPages(id: string, auth?: SourceAuth, opts?: { readUnitId?: string }): Promise<PageRef[]> {
      const normalizedId = normalizeJmId(id);
      const loaded = await delegate();
      const pages = opts?.readUnitId
        ? await loadJmChapterPages(loaded, normalizedId, opts.readUnitId, auth)
        : await loaded.getPages(normalizedId, auth, opts);
      return pages.map((page) => addJmPageTransform(page));
    },

    async getComments(id: string, auth?: SourceAuth, opts?: SourceCommentOpts): Promise<SourceCommentPage> {
      return (await delegate()).getComments!(normalizeJmId(id), auth, opts);
    },

    async resolveImageUrl(ref: PageRef, auth?: SourceAuth): Promise<{ url: string }> {
      return (await delegate()).resolveImageUrl(ref, auth);
    },

    async validateAuth(auth: SourceAuth): Promise<{ ok: boolean; username?: string; reason?: string }> {
      return (await delegate()).validateAuth?.(auth) ?? { ok: false, reason: 'JMComic auth is unavailable' };
    },

    async loginWithForm(request: { username: string; password: string }) {
      return (await delegate()).loginWithForm?.(request) ?? { ok: false, reason: 'JMComic login is unavailable' };
    },

    async listFavorites(auth: SourceAuth, opts: ListOpts): Promise<Page<ComicCard>> {
      return (await delegate()).listFavorites?.(auth, opts) ?? { items: [], page: opts.page ?? 1, hasMore: false };
    },

    async addFavorite(id: string, auth: SourceAuth, category?: number): Promise<void> {
      await (await delegate()).addFavorite?.(normalizeJmId(id), auth, category);
    },

    async removeFavorite(id: string, auth: SourceAuth): Promise<void> {
      await (await delegate()).removeFavorite?.(normalizeJmId(id), auth);
    },
  };

  return source;
}

function jmPluginBootstrap(source: string): string {
  return `${source}
JM.apiDomains = ${JSON.stringify([...JM_API_DOMAINS])};
JM.fallbackServers = ${JSON.stringify([...JM_API_DOMAINS])};
JM.prototype.init = async function bunkoJmInit() {};
new JM();`;
}

function withDefaultSort(query: SearchQuery): SearchQuery {
  const extra = { ...(query.extra ?? {}) };
  if (!Array.isArray(extra.options)) {
    const sort = query.sort ?? (typeof extra.sort === 'string' ? extra.sort : 'mr');
    extra.options = [sort];
  }
  return { ...query, extra };
}

function isEmptyQuery(query: SearchQuery): boolean {
  return (
    !query.q?.trim() &&
    !query.uploader &&
    !query.language &&
    !query.sort &&
    (query.includeTags?.length ?? 0) === 0 &&
    (query.excludeTags?.length ?? 0) === 0 &&
    Object.keys(query.extra ?? {}).every((key) => key === 'page' || key === 'cursor')
  );
}

function mapJmExploreResult(value: unknown, page: number): Page<ComicCard> {
  if (!Array.isArray(value)) {
    return mapVeneraPageResult(value, JMCOMIC_SOURCE_ID, { page });
  }
  const comics = value.flatMap((section) => {
    if (!section || typeof section !== 'object' || Array.isArray(section)) return [];
    const rawComics = (section as Record<string, unknown>).comics;
    return Array.isArray(rawComics) ? rawComics : [];
  });
  return {
    items: comics.map((comic, index) => mapVeneraComicCard(comic, JMCOMIC_SOURCE_ID, `popular comic ${index}`)),
    page,
    hasMore: false,
  };
}

async function loadJmChapterPages(
  source: VeneraComicSource,
  comicId: string,
  readUnitId: string,
  auth?: SourceAuth,
): Promise<PageRef[]> {
  const result = await source.testInternals.callPluginMethod('comic.loadEp', [comicId, readUnitId], auth);
  const pages = normalizeJmPageList(result);
  return pages.map((page, index) => ({
    ...mapVeneraPage(page, JMCOMIC_SOURCE_ID, comicId, index),
    readUnitId,
  }));
}

async function loadJmRankingRecommendations(
  source: VeneraComicSource,
  opts: RecommendationOpts | undefined,
  auth: SourceAuth | undefined,
): Promise<Page<ComicCard>> {
  const page = recommendationPage(opts);
  const rank = allowedExtra(opts, 'rank', JM_RANK_OPTIONS, 'mv_w');
  const result = await source.testInternals.callPluginMethod('categoryComics.ranking.load', [rank, page], auth);
  return mapVeneraPageResult(result, JMCOMIC_SOURCE_ID, { page });
}

async function loadJmCategoryRecommendations(
  source: VeneraComicSource,
  opts: RecommendationOpts | undefined,
  auth: SourceAuth | undefined,
): Promise<Page<ComicCard>> {
  const page = recommendationPage(opts);
  const categoryParam = allowedExtra(opts, 'category', JM_CATEGORY_OPTIONS, '0');
  const sort = allowedExtra(opts, 'sort', JM_SORT_OPTIONS, 'mr');
  const category = JM_CATEGORY_OPTIONS.find((option) => option.value === categoryParam)?.category ?? categoryParam;
  const result = await source.testInternals.callPluginMethod(
    'categoryComics.load',
    [category, categoryParam, [sort], page],
    auth,
  );
  return mapVeneraPageResult(result, JMCOMIC_SOURCE_ID, { page });
}

async function loadJmWeeklyRecommendations(
  source: VeneraComicSource,
  opts: RecommendationOpts | undefined,
  auth: SourceAuth | undefined,
): Promise<Page<ComicCard>> {
  const page = recommendationPage(opts);
  const type = allowedExtra(opts, 'type', JM_WEEKLY_TYPE_OPTIONS, 'manga');
  const latestWeekId = await loadLatestJmWeekId(source, auth);
  const result = await source.testInternals.callPluginMethod(
    'categoryComics.load',
    ['每週必看', undefined, [latestWeekId, type], page],
    auth,
  );
  return mapVeneraPageResult(result, JMCOMIC_SOURCE_ID, { page });
}

async function loadLatestJmWeekId(source: VeneraComicSource, auth: SourceAuth | undefined): Promise<string> {
  const optionGroups = await source.testInternals.callPluginMethod('categoryComics.optionLoader', ['每週必看'], auth);
  const firstOption = firstJmOptionValue(optionGroups);
  return firstOption ?? '';
}

function firstJmOptionValue(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined;
  for (const group of value) {
    if (!group || typeof group !== 'object' || Array.isArray(group)) continue;
    const options = (group as Record<string, unknown>).options;
    if (!Array.isArray(options)) continue;
    for (const option of options) {
      const value = jmOptionValue(option);
      if (value) return value;
    }
  }
  return undefined;
}

function jmOptionValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const separator = value.indexOf('-');
    return (separator > 0 ? value.slice(0, separator) : value).trim() || undefined;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const raw = record.value ?? record.id ?? record.key;
  if (typeof raw === 'string' && raw.trim()) return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return undefined;
}

function recommendationPage(opts: RecommendationOpts | undefined): number {
  return Math.max(1, opts?.page ?? 1);
}

function allowedExtra<T extends ReadonlyArray<{ value: string }>>(
  opts: RecommendationOpts | undefined,
  key: string,
  options: T,
  fallback: T[number]['value'],
): T[number]['value'] {
  const raw = opts?.extra?.[key];
  if (typeof raw === 'string' && options.some((option) => option.value === raw)) {
    return raw as T[number]['value'];
  }
  return fallback;
}

function normalizeJmPageList(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const images = (value as Record<string, unknown>).images;
    if (Array.isArray(images)) return images;
  }
  return [];
}

export function parseJmAlbumLookup(input: string): string | undefined {
  const value = input.trim();
  if (!value) return undefined;

  const idMatch = /^(?:jm)?(\d+)$/i.exec(value);
  if (idMatch?.[1]) return idMatch[1];

  const parsed = safeUrl(value);
  if (!parsed || !JM_ALBUM_HOSTS.some((host) => host === parsed.host)) {
    return undefined;
  }
  const pathMatch = /^\/album\/(\d+)\/?$/.exec(parsed.pathname);
  return pathMatch?.[1];
}

function normalizeJmId(id: string): string {
  return parseJmAlbumLookup(id) ?? id.trim();
}

function jmSourceUrl(id: string): string {
  return `https://18comic.vip/album/${encodeURIComponent(id)}/`;
}

function addJmPageTransform(page: PageRef): PageRef {
  const rows = jmRowReverseRows(page.resolvedUrl, page.readUnitId);
  if (rows <= 1) return page;
  return {
    ...page,
    extra: {
      ...(page.extra ?? {}),
      transform: { kind: 'row-reverse', rows },
    },
  };
}

export function jmRowReverseRows(url: string | undefined, readUnitId: string | undefined): number {
  if (!url || !readUnitId || url.toLowerCase().endsWith('.gif')) return 0;
  const epId = Number(readUnitId);
  if (!Number.isFinite(epId)) return 0;
  const scrambleId = 220_980;
  if (epId < scrambleId) return 0;
  if (epId < 268_850) return 10;
  const pictureName = jmPictureName(url);
  if (!pictureName) return 0;
  const lastCharCode = md5(`${epId}${pictureName}`).charCodeAt(31);
  const modulo = epId > 421_926 ? 8 : 10;
  return (lastCharCode % modulo) * 2 + 2;
}

function jmPictureName(url: string): string | undefined {
  const lastSlash = url.lastIndexOf('/');
  if (lastSlash < 0 || url.length <= lastSlash + 5) return undefined;
  return url.substring(lastSlash + 1, url.length - 5);
}

async function jmProxyFetch(
  url: string,
  opts: ProxyFetchOpts | undefined,
  proxyFetch: (url: string, opts?: ProxyFetchOpts) => Promise<Response>,
): Promise<Response> {
  const parsed = safeUrl(url);
  if (!parsed || !JM_API_DOMAINS.some((host) => host === parsed.host)) {
    return proxyFetch(url, opts);
  }

  let lastResponse: Response | undefined;
  let lastError: unknown;
  for (const host of jmFallbackOrder(preferredJmHost ?? parsed.host)) {
    const next = new URL(parsed);
    next.host = host;
    try {
      const response = await proxyFetch(next.toString(), {
        ...opts,
        timeoutMs: Math.min(opts?.timeoutMs ?? JM_MIRROR_TIMEOUT_MS, JM_MIRROR_TIMEOUT_MS),
      });
      if (await isUsableJmApiResponse(response)) {
        preferredJmHost = host;
        return response;
      }
      lastResponse = response;
    } catch (err) {
      lastError = err;
      if (!(err instanceof NetworkError)) continue;
    }
  }
  if (lastResponse) return lastResponse;
  throw lastError instanceof Error ? lastError : new NetworkError(`JMComic proxy failed for ${url}`);
}

async function isUsableJmApiResponse(response: Response): Promise<boolean> {
  if (response.status === 401) return true;
  if (!response.ok) return false;
  try {
    const payload: unknown = await response.clone().json();
    return (
      typeof payload === 'object' &&
      payload !== null &&
      !Array.isArray(payload) &&
      'data' in payload &&
      typeof payload.data === 'string'
    );
  } catch {
    return false;
  }
}

function safeUrl(url: string): URL | undefined {
  try {
    return new URL(url);
  } catch {
    return undefined;
  }
}

function jmFallbackOrder(host: string): string[] {
  const index = JM_API_DOMAINS.findIndex((candidate) => candidate === host);
  if (index < 0) return [...JM_API_DOMAINS];
  return [...JM_API_DOMAINS.slice(index), ...JM_API_DOMAINS.slice(0, index)];
}

